import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, Building2, Cpu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import logo from "@/assets/logo.webp";
import { servicesData } from "@/data/services";
import { useBusinessMode } from "@/hooks/use-business-mode";

const navLinks = [
  { href: "/ueber-uns", label: "Über uns" },
  { href: "/warum-wir", label: "Warum wir?" },
  { href: "/kontakt", label: "Kontakt" },
];

// Services shown in the "Dienstleistungen" dropdown — exclude IT Helpdesk and Website Erstellung
const HIDDEN_DROPDOWN_SLUGS = new Set(["it-helpdesk", "website-erstellung"]);
const dropdownServices = servicesData.filter((s) => !HIDDEN_DROPDOWN_SLUGS.has(s.slug));

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [desktopServicesOpen, setDesktopServicesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { mode, setMode } = useBusinessMode();

  // Scroll-reveal on IT / dark aesthetic routes. Decided by route only, never by the
  // stored business mode — facility pages stay light even after visiting Digital & IT.
  const path = location.pathname;
  const isITRoute =
    path.startsWith("/digital-it") ||
    path.includes("website-erstellung") ||
    path.startsWith("/webentwicklung");

  const [visible, setVisible] = useState(true);
  const lastScrollRef = useRef(0);

  useEffect(() => {
    if (!isITRoute) {
      setVisible(true);
      return;
    }
    let frame = 0;
    const updateScroll = () => {
      frame = 0;
      const y = window.scrollY;
      const goingUp = y < lastScrollRef.current;
      if (y < 40) setVisible(true);
      else if (goingUp) setVisible(true);
      else setVisible(false);
      lastScrollRef.current = y;
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(updateScroll);
    };
    updateScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [isITRoute]);

  // Dropdown beim Scrollen schließen (bleibt sonst offen hängen)
  useEffect(() => {
    const closeOnScroll = () => {
      if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current);
      setDesktopServicesOpen(false);
    };
    window.addEventListener("scroll", closeOnScroll, { passive: true });
    return () => window.removeEventListener("scroll", closeOnScroll);
  }, []);

  // Dropdown bei Routenwechsel schließen
  useEffect(() => {
    if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current);
    setDesktopServicesOpen(false);
    setIsOpen(false);
  }, [path]);

  const ModeToggle = ({ className = "" }: { className?: string }) => {
    const isDigital = mode === "digital";
    const nextLabel = isDigital
      ? "Zu Gebäude & Service wechseln"
      : "Zu Digital & IT umwandeln";
    const handleToggle = () => {
      const next = isDigital ? "facility" : "digital";
      setMode(next);
      setIsOpen(false);
      navigate(next === "digital" ? "/digital-it" : "/");
    };
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            aria-label={nextLabel}
            onClick={handleToggle}
            className={`group relative inline-flex h-8 w-14 items-center rounded-full border border-border/40 dark:border-white/20 bg-background/60 dark:bg-white/10 backdrop-blur-xl shadow-[0_4px_20px_-6px_rgba(0,0,0,0.25)] transition-colors hover:border-primary/60 ${className}`}
          >
            <span
              className={`absolute top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground dark:bg-gradient-to-br dark:from-violet-500 dark:to-fuchsia-500 dark:text-white shadow-soft transition-transform duration-300 ${
                isDigital ? "translate-x-7" : "translate-x-1"
              }`}
            >
              {isDigital ? <Cpu className="w-3.5 h-3.5" /> : <Building2 className="w-3.5 h-3.5" />}
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs font-medium">
          {nextLabel}
        </TooltipContent>
      </Tooltip>
    );
  };

  const scrollToSection = (href: string) => {
    setIsOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + href);
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleDesktopEnter = () => {
    if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current);
    setDesktopServicesOpen(true);
  };

  const handleDesktopLeave = () => {
    dropdownTimeout.current = setTimeout(() => setDesktopServicesOpen(false), 150);
  };

  // Universal Apple-style glass pill styling — adapts to light/dark automatically
  const linkClass =
    "px-4 py-2 rounded-full text-sm font-medium text-foreground/85 hover:text-foreground bg-background/60 dark:bg-white/10 border border-border/40 dark:border-white/15 backdrop-blur-xl shadow-[0_4px_20px_-6px_rgba(0,0,0,0.25)] hover:bg-background/80 dark:hover:bg-white/15 transition-all";

  const navClass = isITRoute
    ? `fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-transparent ${
        visible ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
      }`
    : "fixed top-0 left-0 right-0 z-50 bg-transparent";

  return (
    <nav className={navClass}>
      <div className="container mx-auto px-4 pt-3">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link
            to={isITRoute ? "/digital-it" : "/"}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity px-3 py-1.5 rounded-full bg-background/60 dark:bg-white/10 border border-border/40 dark:border-white/15 backdrop-blur-xl shadow-[0_4px_20px_-6px_rgba(0,0,0,0.25)]"
          >
            <img
              src={logo}
              alt="ManageMatch (Manage Match) Logo"
              className="h-8 w-auto"
              width={48}
              height={32}
              style={{ mixBlendMode: isITRoute ? "screen" : "multiply" }}
            />
            <span className="font-outfit font-bold text-lg hidden sm:block text-foreground">
              ManageMatch
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            <div
              className="relative"
              onMouseEnter={handleDesktopEnter}
              onMouseLeave={handleDesktopLeave}
            >
              <Link
                to="/dienstleistungen"
                className={`${linkClass} flex items-center gap-1`}
              >
                Dienstleistungen
                <ChevronDown className="w-4 h-4" />
              </Link>
              {desktopServicesOpen && (
                <div className="absolute left-0 top-full pt-3 z-50">
                  <div className="rounded-2xl p-2 grid grid-cols-2 gap-1 w-[420px] bg-background/70 dark:bg-white/10 border border-border/40 dark:border-white/15 backdrop-blur-2xl shadow-[0_10px_40px_-8px_rgba(0,0,0,0.4)]">
                    {dropdownServices.map((service) => {
                      const Icon = service.icon;
                      return (
                        <Link
                          key={service.slug}
                          to={`/dienstleistungen/${service.slug}`}
                          onClick={() => setDesktopServicesOpen(false)}
                          className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-foreground/85 hover:text-foreground hover:bg-foreground/5 dark:hover:bg-white/10 transition-colors"
                        >
                          <Icon className="w-4 h-4 shrink-0" />
                          <span className="truncate">{service.title}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {navLinks.map((link) => (
              <Link key={link.href} to={link.href} className={linkClass}>
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA / Toggle */}
          <div className="hidden md:flex items-center gap-2">
            <ModeToggle />
            <Button
              onClick={() => scrollToSection("#contact")}
              className="gradient-mint text-primary-foreground font-semibold shadow-soft hover:shadow-glow transition-all rounded-full"
            >
              Angebot anfordern
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <ModeToggle className="scale-90 origin-right" />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-full bg-background/60 dark:bg-white/10 border border-border/40 dark:border-white/15 backdrop-blur-xl text-foreground"
              aria-label={isOpen ? "Menü schließen" : "Menü öffnen"}
            >
              {isOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-4 mt-2 rounded-2xl px-4 animate-fade-up bg-background/70 dark:bg-white/10 border border-border/40 dark:border-white/15 backdrop-blur-2xl">
            <div className="flex flex-col gap-1">
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                className="flex items-center justify-between font-medium text-left py-2 text-foreground/85 hover:text-foreground"
              >
                Dienstleistungen
                <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
              </button>
              {servicesOpen && (
                <div className="pl-4 space-y-1 mb-2">
                  {dropdownServices.map((service) => {
                    const Icon = service.icon;
                    return (
                      <Link
                        key={service.slug}
                        to={`/dienstleistungen/${service.slug}`}
                        onClick={() => setIsOpen(false)}
                        className="flex items-center gap-3 py-2 transition-colors text-foreground/75 hover:text-foreground"
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm font-medium">{service.title}</span>
                      </Link>
                    );
                  })}
                  <Link
                    to="/dienstleistungen"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-3 py-2 font-medium text-sm text-primary dark:text-violet-300"
                  >
                    Alle Dienstleistungen →
                  </Link>
                </div>
              )}

              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  className="font-medium text-left py-2 text-foreground/85 hover:text-foreground"
                >
                  {link.label}
                </Link>
              ))}
              <Button
                onClick={() => scrollToSection("#contact")}
                className="gradient-mint text-primary-foreground font-semibold w-full mt-2 rounded-full"
              >
                Angebot anfordern
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
